import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, CheckCircle2, Clock3, MapPin, MessageCircle, Star, XCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";
import { useNavigate } from "react-router-dom";

const MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const formatDate = (iso) => {
  if (!iso) return "";
  const [y,m,d] = iso.split("-");
  return `${parseInt(d,10)}. ${MONTHS[parseInt(m,10)-1]} ${y}`;
};

const steps = [
  { key: "sent", label: "Anfrage gesendet" },
  { key: "confirmed", label: "Bestätigt" },
  { key: "booked", label: "Gebucht" },
  { key: "completed", label: "Abgeschlossen" },
];

function currentStep(req) {
  if (req.status === "completed") return 3;
  if (req.status === "accepted") return 2;
  if (req.status === "pending" && (req.applicants || []).length > 0) return 1;
  return 0;
}

function RatingBox({ req, onDone }) {
  const { user } = useUser();
  const [rating, setRating] = useState(req.rating || 0);
  const [comment, setComment] = useState(req.rating_comment || "");
  const [saving, setSaving] = useState(false);
  if (req.rating) return <p className="text-sm text-gray-600 mt-3">Bewertet mit <strong>{req.rating}/5</strong>.</p>;
  const submit = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      await api.post(`/requests/${req.id}/rate?client_id=${user.id}`, { rating, comment });
      onDone();
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <p className="text-sm font-semibold text-gray-900 mb-2">Einsatz bewerten</p>
      <div className="flex gap-1 mb-3">{[1,2,3,4,5].map((n) => <button key={n} onClick={() => setRating(n)} className={n <= rating ? "text-amber-500" : "text-gray-300"}><Star size={22} fill="currentColor" /></button>)}</div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optionaler Kommentar" className="w-full px-3 py-2 border border-gray-200 rounded-xl resize-none text-sm" />
      <button onClick={submit} disabled={!rating || saving} className="mt-2 px-4 py-2 rounded-xl bg-[#14B8A6] text-sm font-semibold disabled:opacity-50">Bewertung speichern</button>
    </div>
  );
}

export default function ClientRequestsV2() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get(`/requests?user_id=${user.id}&role=client`).then((r) => setRequests(r.data || [])).finally(() => setLoading(false));
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const complete = async (id) => {
    await api.post(`/requests/${id}/complete?client_id=${user.id}`);
    load();
  };

  const withdraw = async (id) => {
    if (!window.confirm("Anfrage wirklich zurückziehen? Bei bereits verbindlich angenommenen Buchungen können Ausfallhonorare anfallen.")) return;
    await api.post(`/requests/${id}/withdraw?client_id=${user.id}`);
    load();
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8"><h1 className="text-3xl font-bold text-gray-900">Meine Anfragen</h1><p className="text-gray-500 mt-1">Vom Senden bis zum abgeschlossenen Einsatz.</p></div>
        {loading ? <div className="py-16 text-center text-gray-400">Anfragen werden geladen…</div> : requests.length === 0 ? (
          <div className="py-16 text-center border border-gray-200 rounded-3xl bg-white"><p className="font-semibold text-gray-700">Noch keine Anfragen</p><button onClick={() => navigate("/kunde/suche")} className="mt-4 px-5 py-3 rounded-xl bg-[#14B8A6] font-semibold">Dolmetscher:in finden</button></div>
        ) : <div className="space-y-5">
          {requests.map((req, index) => {
            const step = currentStep(req);
            const inactive = ["rejected","withdrawn"].includes(req.status);
            return (
              <motion.article key={req.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div><h2 className="font-bold text-gray-900 text-lg">{req.interpreter_name || (req.is_broadcast ? "Schnell-Anfrage" : "Dolmetscher:in")}</h2><p className="text-sm text-gray-600 mt-1">{req.event_type}</p></div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${req.status === "accepted" ? "bg-green-100 text-green-700" : req.status === "completed" ? "bg-[#F0FDFA] text-[#0D9488]" : req.status === "rejected" ? "bg-red-100 text-red-600" : req.status === "withdrawn" ? "bg-gray-100 text-gray-500" : "bg-amber-50 text-amber-700"}`}>{req.status === "accepted" ? "Gebucht" : req.status === "completed" ? "Abgeschlossen" : req.status === "rejected" ? "Abgelehnt" : req.status === "withdrawn" ? "Zurückgezogen" : "In Bearbeitung"}</span>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2"><Clock3 size={15} /> {formatDate(req.event_date)}{req.event_time ? ` · ${req.event_time}${req.event_time_end ? `–${req.event_time_end}` : ""} Uhr` : ""}</p>
                  <p className="flex items-center gap-2"><MapPin size={15} /> {req.event_location}</p>
                </div>

                {!inactive && <div className="mt-5 grid grid-cols-4 gap-2">
                  {steps.map((s, i) => <div key={s.key} className="min-w-0"><div className={`h-1.5 rounded-full ${i <= step ? "bg-[#14B8A6]" : "bg-gray-200"}`} /><p className={`text-[11px] mt-1.5 ${i <= step ? "text-gray-800 font-medium" : "text-gray-400"}`}>{s.label}</p></div>)}
                </div>}

                {req.is_broadcast && req.status === "pending" && (
                  <div className="mt-4 rounded-xl bg-[#F5F3FF] border border-violet-100 p-4">
                    <p className="text-sm font-semibold text-violet-700">{(req.applicants || []).length} Rückmeldung{(req.applicants || []).length === 1 ? "" : "en"}</p>
                    <div className="mt-2 space-y-2">{(req.applicants || []).map((a) => <div key={a.interpreter_id} className="flex items-center justify-between gap-3 bg-white border border-violet-100 rounded-xl p-3"><div><p className="text-sm font-semibold text-gray-900">{a.name}</p><p className="text-xs text-gray-500">{a.service_city}{a.hourly_rate ? ` · ${a.hourly_rate} €/Std.` : ""}</p></div><button onClick={async () => { await api.post(`/requests/${req.id}/select-applicant`, { interpreter_id: a.interpreter_id }); load(); }} className="px-3 py-2 rounded-lg bg-violet-600 text-white text-xs font-semibold">Auswählen</button></div>)}</div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
                  {req.status === "accepted" && <><button onClick={() => navigate(`/chat/${req.id}`)} className="px-4 py-2 rounded-xl bg-gray-100 text-sm font-medium flex items-center gap-2"><MessageCircle size={16} /> Chat öffnen</button><button onClick={() => complete(req.id)} className="px-4 py-2 rounded-xl bg-[#14B8A6] text-sm font-semibold flex items-center gap-2"><CheckCircle2 size={16} /> Einsatz bestätigen</button></>}
                  {["pending","accepted"].includes(req.status) && <button onClick={() => withdraw(req.id)} className="px-3 py-2 rounded-xl text-red-600 text-sm font-medium">Anfrage zurückziehen</button>}
                </div>

                {req.status === "completed" && <RatingBox req={req} onDone={load} />}
                {req.status === "rejected" && <p className="mt-3 text-sm text-red-600 flex items-center gap-2"><XCircle size={16} /> Die Anfrage wurde abgelehnt.</p>}
                {req.status === "completed" && <p className="mt-3 text-sm text-[#0D9488] flex items-center gap-2"><Check size={16} /> Einsatz abgeschlossen.</p>}
              </motion.article>
            );
          })}
        </div>}
      </div>
    </Layout>
  );
}
